import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import RegistroMantenedora from "./RegistroMantenedora";
import FormularioNovoProcesso from "./FormularioNovoProcesso";
import { api } from "@api/api";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";
import { CondicaoContext } from "../../../contexto";
import { lerTipoInstituicao } from "../../../processos/tipoInstituicao";
import {
  lerSolicitacaoAtiva,
  prepararSolicitacao,
} from "./solicitacoesProcesso";

export default function PaginaProcessos() {
  const { userId } = useContext(CondicaoContext);
  const navigate = useNavigate();
  const { key: chaveNavegacao, state: estadoNavegacao } = useLocation();
  const retomarSelecao = estadoNavegacao?.retomarSelecao === true;
  const restauradoRef = useRef(null);
  const [escolas, setEscolas] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [mantenedora, setMantenedora] = useState(null);
  const [mantenedoraSelecionada, setMantenedoraSelecionada] = useState("");
  const [tipoInstituicao, setTipoInstituicao] = useState("");
  const [processType, setProcessType] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [showRegistro, setShowRegistro] = useState(true);
  const [aguardandoAprovacao, setAguardandoAprovacao] = useState(false);
  const [mensagemEscoActivate, setMensagemEscoActivate] = useState("");
  const [loading, setLoading] = useState(true);
  const [carga, setCarga] = useState({ cadastro: true, mantenedora: true, escolas: true, tipos: true });
  const [erroCarga, setErroCarga] = useState("");
  const [recarregar, setRecarregar] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  useEffect(() => {
    if (!userId) return undefined;
    let ativo = true;
    const controller = new AbortController();
    const carregar = async () => {
      setLoading(true);
      setCarga({ cadastro: true, mantenedora: true, escolas: true, tipos: true });
      setErroCarga("");
      try {
        const config = { withCredentials: true, signal: controller.signal };
        const { data: status } = await axios.get(api.verify(), config);
        if (!ativo) return;
        setShowRegistro(!status.isRegistered);
        setAguardandoAprovacao(Boolean(status.isRegistered && !status.ativo));
        if (!status.isRegistered || !status.ativo) return;
        setCarga((atual) => ({ ...atual, cadastro: false }));
        const consultaEscolas = axios.get(api.listUserSchools(), config).then((resposta) => {
          if (!Array.isArray(resposta.data?.escolas)) throw new Error('Lista de escolas inválida.');
          if (ativo) {
            const unicas = resposta.data.escolas.reduce((lista, item) =>
              lista.some((escola) => escola.nome === item.nome) ? lista : [...lista, item], []);
            setEscolas(unicas);
            setMensagemEscoActivate(resposta.data.EscoActivate ? '' :
              'Para mais tipos de processo, registre uma escola em sua mantenedora.');
            setCarga((atual) => ({ ...atual, escolas: false }));
          }
          return resposta;
        });
        const consultaTipos = axios.get(api.listGeneralProcesses(), config);
        const consultaMantenedora = axios.get(api.maintainerData(), config).then((resposta) => {
          if (!resposta.data?.id) throw new Error('Mantenedora não encontrada.');
          if (ativo) {
            setMantenedora(resposta.data);
            setCarga((atual) => ({ ...atual, mantenedora: false }));
          }
        });
        // O filtro do catálogo ainda depende da autorização das escolas.
        const consultaCatalogo = Promise.all([consultaEscolas, consultaTipos]).then(([respEscolas, respTipos]) => {
          if (!Array.isArray(respTipos.data)) throw new Error('Catálogo de processos inválido.');
          if (!ativo) return;
          const ordenados = [...respTipos.data].sort((a, b) =>
            (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }));
          setProcessTypes(respEscolas.data.EscoActivate ? ordenados :
            ordenados.filter((item) => item.nome === 'Credenciamento'));
          setCarga((atual) => ({ ...atual, tipos: false }));
        });
        await Promise.all([consultaCatalogo, consultaMantenedora]);
      } catch (error) {
        if (ativo)
          setErroCarga(
            "Não foi possível carregar as informações. Tente novamente.",
          );
      } finally {
        if (ativo) setLoading(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
      controller.abort();
    };
  }, [userId, recarregar]);
  useEffect(() => {
    // Nova entrada pelo menu começa vazia; Voltar indica explicitamente a retomada.
    restauradoRef.current = null;
    setMantenedoraSelecionada('');
    setTipoInstituicao('');
    setProcessType('');
    setSelectedSchool('');
  }, [userId, chaveNavegacao]);
  useEffect(() => {
    if (!retomarSelecao || loading || !userId || !mantenedora || restauradoRef.current === userId)
      return;
    try {
      const anterior = lerSolicitacaoAtiva(userId);
      if (!anterior) return;
      const tipo = processTypes.find(
        (item) => String(item.id) === String(anterior.processType),
      );
      const categoria = lerTipoInstituicao(tipo);
      if (!categoria) return;
      if (
        anterior.mantenedoraId &&
        String(anterior.mantenedoraId) !== String(mantenedora.id)
      )
        return;
      const escola = escolas.find(
        (item) => String(item.id) === String(anterior.selectedSchool),
      );
      // Não transportar uma seleção de escola para um tipo de mantenedora reclassificado.
      if (categoria === "escola" && !escola) return;
      if (categoria === "mantenedora" && anterior.selectedSchool) return;
      setMantenedoraSelecionada(mantenedora.id);
      setTipoInstituicao(categoria);
      setProcessType(tipo.id);
      setSelectedSchool(escola?.id || "");
      restauradoRef.current = userId;
    } catch {
      setSnackbarMessage(
        "Não foi possível recuperar os dados temporários do processo.",
      );
    }
  }, [loading, userId, mantenedora, processTypes, escolas, retomarSelecao, chaveNavegacao]);
  const tiposDisponiveis = processTypes.filter(
    (item) =>
      lerTipoInstituicao(item) === tipoInstituicao && Boolean(tipoInstituicao),
  );
  const processoSelecionado = tiposDisponiveis.find(
    (item) => String(item.id) === String(processType),
  );
  const escolaSelecionada = escolas.find(
    (item) => String(item.id) === String(selectedSchool),
  );
  const valido = Boolean(
    !loading && !erroCarga && mantenedoraSelecionada &&
      String(mantenedoraSelecionada) === String(mantenedora?.id) &&
      lerTipoInstituicao(tipoInstituicao) &&
      processoSelecionado &&
      (tipoInstituicao === "mantenedora" || escolaSelecionada),
  );
  const mudarCategoria = (event) => {
    restauradoRef.current = userId;
    setTipoInstituicao(event.target.value);
    setProcessType("");
    setSelectedSchool("");
  };
  const continuar = (event) => {
    event.preventDefault();
    if (!valido) {
      setSnackbarMessage("Por favor, preencha todos os campos obrigatórios!");
      return;
    }
    try {
      prepararSolicitacao(userId, {
        processType,
        nomeProcesso: processoSelecionado.nome,
        descricaoProcesso: processoSelecionado.descricao || "",
        selectedSchool: tipoInstituicao === "escola" ? selectedSchool : null,
        tipoInstituicao,
        mantenedoraId: mantenedora.id,
        nomeMantenedora: mantenedora.nome,
        nomeEscola:
          tipoInstituicao === "escola" ? escolaSelecionada.nome : null,
      });
      navigate("/novoProcesso/documentos");
    } catch (error) {
      setSnackbarMessage(
        error.message || "Não foi possível preparar a solicitação.",
      );
    }
  };
  return (
    <TransicaoPagina variante="entrada">
      <Container maxWidth={false} disableGutters sx={!loading && !erroCarga && (showRegistro || aguardandoAprovacao) ? { mt: 2, mb: 3 } : { mt: 0, mb: 0 }}>
        {!loading && !erroCarga && (showRegistro || aguardandoAprovacao) ? (
          <>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
              >
                {showRegistro ? "Registrar Mantenedora" : "Criar Novo Processo"}
              </Typography>
            </Box>
            <Paper
              elevation={4}
              sx={{
                borderRadius: "16px",
                padding: { xs: "20px", md: "30px" },
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                background:
                  "linear-gradient(to bottom right, var(--sear-superficie, #ffffff), var(--sear-superficie-azul, #f5f9ff))",
              }}
            >
              {showRegistro ? (
                <RegistroMantenedora
                  onNext={() => setRecarregar((valor) => valor + 1)}
                />
              ) : (
                <Box sx={{ textAlign: "center", p: 4 }}>
                  <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    Seu cadastro está em análise
                  </Typography>
                  <Typography variant="body1">
                    Por favor, aguarde a aprovação do seu cadastro para
                    continuar.
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        ) : (
          <FormularioNovoProcesso
            carregando={loading}
            carga={carga}
            erroCarga={erroCarga}
            onRecarregar={() => setRecarregar((valor) => valor + 1)}
            mantenedora={mantenedora}
            mantenedoraSelecionada={mantenedoraSelecionada}
            onMantenedora={(event) => {
              restauradoRef.current = userId;
              setMantenedoraSelecionada(event.target.value);
            }}
            tipoInstituicao={tipoInstituicao}
            onTipoInstituicao={mudarCategoria}
            escolas={escolas}
            selectedSchool={selectedSchool}
            onEscola={(event) => { restauradoRef.current = userId; setSelectedSchool(event.target.value); }}
            tipos={tiposDisponiveis}
            processType={processType}
            onProcesso={(event) => { restauradoRef.current = userId; setProcessType(event.target.value); }}
            descricao={processoSelecionado?.descricao || ""}
            mensagem={mensagemEscoActivate}
            onContinuar={continuar}
            onVoltar={() => navigate("/home/processos")}
            valido={valido}
          />
        )}
        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setSnackbarMessage("")} severity="info">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </TransicaoPagina>
  );
}
